import React, { useEffect, useState } from 'react';

interface DashboardPageProps {
  email: string;
  profile: string | null;
  error: string | null;
  onUpdateProfile: (profile: string) => Promise<void>;
  onSignOut: () => void;
}

export function DashboardPage({ email, profile, error, onUpdateProfile, onSignOut }: DashboardPageProps): React.JSX.Element {
  const [profileDraft, setProfileDraft] = useState(profile ?? '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setProfileDraft(profile ?? '');
  }, [profile]);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setIsSaving(true);
    try {
      await onUpdateProfile(profileDraft);
    } catch {
      // AuthContext stores a user-visible, typed error state.
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="dash-page">
      <img className="dash-watermark" src="/synphant-watermark.png" alt="" />

      <div className="dash-topbar">
        <div className="dash-topbar-brand">
          <img className="dash-topbar-badge" src="/synphant-badge.png" alt="Synphant" />
          <div className="dash-topbar-wordmark">
            <span className="w-syn">Syn</span>
            <span className="w-phant">phant</span>
          </div>
        </div>
        <div className="dash-topbar-right">
          <span className="dash-topbar-email">{email}</span>
          <button className="dash-signout" onClick={onSignOut}>Sign out</button>
        </div>
      </div>

      <main className="dash-main">
        <h1 className="dash-welcome">Welcome to Synphant</h1>
        <p className="dash-tagline">Your web account is ready.</p>

        <section className="dash-card" aria-labelledby="account-heading">
          <h2 id="account-heading" className="dash-card-title">Account</h2>
          <p>Signed in as {email}</p>
          <form onSubmit={(event) => void handleSubmit(event)}>
            <label className="dash-code-label" htmlFor="profile">Profile</label>
            <textarea
              id="profile"
              className="auth-input"
              value={profileDraft}
              onChange={(event) => setProfileDraft(event.target.value)}
              disabled={isSaving}
              rows={4}
              aria-describedby={error ? 'profile-error' : undefined}
            />
            {error && <p id="profile-error" className="auth-error" role="alert">{error}</p>}
            <button type="submit" className="auth-btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save profile'}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
